import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const USERS_COLLECTION = 'users';

/**
 * Register a new user with Firebase Auth & Firestore Profile
 */
export const registerUser = async ({ email, password, firstName, lastName }) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    const displayName = `${firstName || ''} ${lastName || ''}`.trim();
    if (displayName) {
      await updateProfile(firebaseUser, { displayName });
    }

    const userData = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      firstName: firstName || '',
      lastName: lastName || '',
      role: email === 'admin@rangaara.com' ? 'admin' : 'user',
      createdAt: serverTimestamp()
    };

    await setDoc(doc(db, USERS_COLLECTION, firebaseUser.uid), userData);

    const token = await firebaseUser.getIdToken();
    return { user: userData, token };
  } catch (error) {
    console.error('Error registering user in Firebase:', error);
    if (error.code === 'auth/configuration-not-found' || error.message?.includes('configuration-not-found')) {
      throw new Error('Firebase Email/Password Authentication is not enabled in Firebase Console. Please go to Firebase Console -> Authentication -> Sign-in method, and enable "Email/Password".');
    }
    throw error;
  }
};

/**
 * Login user with Firebase Auth
 */
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Fetch profile data from Firestore
    const userDocRef = doc(db, USERS_COLLECTION, firebaseUser.uid);
    const userDoc = await getDoc(userDocRef);

    let userData = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      firstName: firebaseUser.displayName?.split(' ')[0] || 'User',
      lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
      role: email === 'admin@rangaara.com' ? 'admin' : 'user'
    };

    if (userDoc.exists()) {
      userData = { ...userData, ...userDoc.data() };
    }

    const token = await firebaseUser.getIdToken();
    return { user: userData, token };
  } catch (error) {
    console.error('Error logging in with Firebase:', error);
    if (error.code === 'auth/configuration-not-found' || error.message?.includes('configuration-not-found')) {
      throw new Error('Firebase Email/Password Authentication is not enabled in Firebase Console. Please go to Firebase Console -> Authentication -> Sign-in method, and enable "Email/Password".');
    }
    throw error;
  }
};

/**
 * Logout user
 */
export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out from Firebase:', error);
    throw error;
  }
};

/**
 * Get current authenticated user profile
 */
export const getCurrentUserProfile = async (firebaseUser) => {
  if (!firebaseUser) return null;
  try {
    const userDocRef = doc(db, USERS_COLLECTION, firebaseUser.uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      firstName: firebaseUser.displayName || 'User',
      role: firebaseUser.email === 'admin@rangaara.com' ? 'admin' : 'user'
    };
  } catch (error) {
    console.error('Error fetching user profile from Firestore:', error);
    return null;
  }
};

/**
 * Listen to Auth state changes
 */
export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      const profile = await getCurrentUserProfile(firebaseUser);
      const token = await firebaseUser.getIdToken();
      callback({ user: profile, token, firebaseUser });
    } else {
      callback({ user: null, token: null, firebaseUser: null });
    }
  });
};

/**
 * Send password reset email
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

/**
 * Change current user password with reauthentication
 */
export const changeUserPassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No active user logged in.');
    }

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
    return { success: true };
  } catch (error) {
    console.error('Error changing user password:', error);
    throw error;
  }
};

